import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Search, Tag, FlaskConical, Edit3, Layers, Rocket, CheckCircle, Clock, Zap } from 'lucide-react';

const workflowSteps = [
  {
    id: 'gathering',
    title: 'Gathering',
    description: 'Discover and capture content from various sources',
    icon: Search,
    color: 'blue',
    path: '/elevation/workflow/gathering',
    engines: ['Prospector Engine'],
    operations: ['OCR Text', 'OCR Image', 'ChatGPT Import', 'Notion Sync']
  },
  {
    id: 'tagging',
    title: 'Tagging',
    description: 'Add metadata and structure to collected content',
    icon: Tag,
    color: 'green',
    path: '/elevation/workflow/tagging',
    engines: ['Knowledge Engine'],
    operations: ['Auto Tagging', 'Entity Extraction', 'Notion Database']
  },
  {
    id: 'formulating',
    title: 'Formulating',
    description: 'Research and develop concepts into structured content',
    icon: FlaskConical,
    color: 'purple',
    path: '/elevation/workflow/formulating',
    engines: ['Authoring Engine'],
    operations: ['Research Topic', 'Create Outline', 'Content Planning']
  },
  {
    id: 'creating',
    title: 'Creating',
    description: 'Author and design the final content',
    icon: Edit3,
    color: 'orange',
    path: '/elevation/workflow/creating',
    engines: ['Studio Engine'],
    operations: ['Generate Content', 'Create Visuals', 'Design Assets']
  },
  {
    id: 'applying',
    title: 'Applying',
    description: 'Integrate content into various formats and contexts',
    icon: Layers,
    color: 'indigo',
    path: '/elevation/workflow/applying',
    engines: ['Assembly Engine'],
    operations: ['Format Content', 'Create Package', 'Integrate Assets']
  },
  {
    id: 'publishing',
    title: 'Publishing',
    description: 'Distribute content across channels and platforms',
    icon: Rocket,
    color: 'red',
    path: '/elevation/workflow/publishing',
    engines: ['Publishing Engine'],
    operations: ['Publish Web', 'Social Media', 'Email Campaign']
  }
];

const getColorClasses = (color: string) => {
  const colors = {
    blue: {
      bg: 'bg-[var(--slate-dim)]',
      border: 'border-[var(--slate)]',
      text: 'text-[var(--text-sec)]',
      badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
      hover: 'hover:bg-[var(--slate-dim)]'
    },
    green: {
      bg: 'bg-[var(--green-dim)]',
      border: 'border-[var(--green)]',
      text: 'text-[var(--green)] dark:text-[var(--text-ter)]',
      badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
      hover: 'hover:bg-[var(--green-dim)]'
    },
    purple: {
      bg: 'bg-[var(--slate-dim)]',
      border: 'border-[var(--slate)]',
      text: 'text-[var(--text-sec)]',
      badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
      hover: 'hover:bg-[var(--slate-dim)]'
    },
    orange: {
      bg: 'bg-[var(--mustard-dim)]',
      border: 'border-[var(--mustard)]',
      text: 'text-[var(--mustard)]',
      badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
      hover: 'hover:bg-[var(--mustard-dim)]'
    },
    indigo: {
      bg: 'bg-[var(--slate-dim)]',
      border: 'border-[var(--slate)]',
      text: 'text-[var(--text-sec)]',
      badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
      hover: 'hover:bg-[var(--slate-dim)]'
    },
    red: {
      bg: 'bg-[var(--coral-dim)]',
      border: 'border-[var(--coral)]',
      text: 'text-[var(--coral)]',
      badge: 'bg-[var(--coral-dim)] text-[var(--coral)]',
      hover: 'hover:bg-[var(--coral-dim)]'
    }
  };
  return colors[color as keyof typeof colors] || colors.blue;
};

export default function Workflow() {
  return (
    <div className="min-h-screen bg-[var(--surface)]">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/elevation/app" className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Run
          </Link>
          <h1 className="text-3xl font-bold text-[var(--text-pri)] mb-2">Content Processing Workflow</h1>
          <p className="text-[var(--text-sec)]">Follow the complete journey from content discovery to publication. Each step builds on the previous one.</p>
        </div>

        {/* Workflow Overview */}
        <div className="bg-[var(--bg-raised)] shadow-sm border p-8 mb-8">
          <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-6 text-center">Content Creation Pipeline</h2>
          
          {/* Desktop Flow */}
          <div className="hidden lg:grid grid-cols-6 gap-4">
            {workflowSteps.map((step, index) => {
              const Icon = step.icon;
              const colors = getColorClasses(step.color);
              
              return (
                <React.Fragment key={step.id}>
                  <Link
                    to={step.path}
                    className={`${colors.bg} ${colors.border} border-2  p-6 ${colors.hover} transition-all duration-300 hover:scale-105 cursor-pointer`}
                  >
                    <div className="text-center">
                      <div className={`inline-flex items-center justify-center w-12 h-12 ${colors.badge}  mb-4`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className={`font-bold text-lg ${colors.text} mb-2`}>
                        {step.title}
                      </h3>
                      <p className={`text-sm ${colors.text} mb-3`}>
                        {step.description}
                      </p>
                      <div className="space-y-1">
                        {step.engines.map((engine, engineIndex) => (
                          <span
                            key={engineIndex}
                            className="px-2 py-1 bg-[var(--bg-raised)] rounded text-xs text-[var(--text-sec)] border"
                          >
                            {engine}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                  
                  {/* Arrow between steps */}
                  {index < workflowSteps.length - 1 && (
                    <div className="flex items-center justify-center">
                      <ArrowRight className="h-6 w-6 text-[var(--text-ter)]" />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Mobile Flow */}
          <div className="lg:hidden space-y-4">
            {workflowSteps.map((step) => {
              const Icon = step.icon;
              const colors = getColorClasses(step.color);
              
              return (
                <React.Fragment key={step.id}>
                  <Link
                    to={step.path}
                    className={`${colors.bg} ${colors.border} border-2  p-6 ${colors.hover} transition-all duration-300 block`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`inline-flex items-center justify-center w-12 h-12 ${colors.badge}  flex-shrink-0`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-bold text-lg ${colors.text} mb-1`}>
                          {step.title}
                        </h3>
                        <p className={`text-sm ${colors.text} mb-2`}>
                          {step.description}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {step.engines.map((engine, engineIndex) => (
                            <span
                              key={engineIndex}
                              className="px-2 py-1 bg-[var(--bg-raised)] rounded text-xs text-[var(--text-sec)] border"
                            >
                              {engine}
                            </span>
                          ))}
                        </div>
                      </div>
                      <ArrowRight className="h-5 w-5 text-[var(--text-ter)]" />
                    </div>
                  </Link>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Workflow Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[var(--slate-dim)] flex items-center justify-center">
                <Zap className="h-5 w-5 text-[var(--text-ter)]" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--text-pri)]">6 Processing Steps</h3>
            </div>
            <p className="text-[var(--text-sec)]">Complete pipeline from discovery to publication</p>
          </div>
          
          <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[var(--green-dim)] flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-[var(--green)]" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--text-pri)]">Multiple Engines</h3>
            </div>
            <p className="text-[var(--text-sec)]">Specialized engines for each phase of the workflow</p>
          </div>
          
          <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[var(--slate-dim)] flex items-center justify-center">
                <Clock className="h-5 w-5 text-[var(--text-ter)]" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--text-pri)]">Real-time Processing</h3>
            </div>
            <p className="text-[var(--text-sec)]">Live monitoring and execution of workflow steps</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link
              to="/elevation/workflow/gathering"
              className="p-4 border border-[var(--rule)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-colors text-left"
            >
              <div className="flex items-center gap-2 mb-2">
                <Search className="h-4 w-4 text-[var(--text-ter)]" />
                <span className="font-medium text-sm">Start Gathering</span>
              </div>
              <p className="text-xs text-[var(--text-sec)]">Begin content discovery and capture</p>
            </Link>
            
            <Link
              to="/elevation/workflow/creating"
              className="p-4 border border-[var(--rule)] hover:border-[var(--mustard)] hover:bg-[var(--mustard-dim)] transition-colors text-left"
            >
              <div className="flex items-center gap-2 mb-2">
                <Edit3 className="h-4 w-4 text-[var(--mustard)]" />
                <span className="font-medium text-sm">Jump to Creating</span>
              </div>
              <p className="text-xs text-[var(--text-sec)]">Skip to content authoring</p>
            </Link>
            
            <Link
              to="/elevation/workflow/publishing"
              className="p-4 border border-[var(--rule)] hover:border-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors text-left"
            >
              <div className="flex items-center gap-2 mb-2">
                <Rocket className="h-4 w-4 text-[var(--coral)]" />
                <span className="font-medium text-sm">Go to Publishing</span>
              </div>
              <p className="text-xs text-[var(--text-sec)]">Publish existing content</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
